import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const PackagesSection = () => {
  const { t } = useTranslation();

  const plans = [
    {
      title: 'Starter',
      description: 'Ideal für kleine Unternehmen mit bis zu 5 Außendienstmitarbeitern.',
      price: '249€',
      period: 'pro Monat',
      features: [
        'Bis zu 5 Benutzer',
        'Digitale Auftragserfassung',
        'Grundlegende ERP-Integration',
        'Kundenverwaltung',
        'Artikelkatalog',
        'Einfache Berichterstattung',
        'E-Mail und Telefon-Support',
        'Jährliche Updates'
      ],
      limitations: [
        'Keine Offline-Funktionalität',
        'Keine individuellen Anpassungen',
        'Keine kundenspezifischen Preise'
      ],
      buttonText: 'Starter auswählen',
      featured: false
    },
    {
      title: 'Professional',
      description: 'Die perfekte Lösung für wachsende Unternehmen mit mittlerem Außendienst.',
      price: '449€',
      period: 'pro Monat',
      features: [
        'Bis zu 15 Benutzer',
        'Alle Starter-Funktionen',
        'Volle ERP-Integration',
        'Offline-Funktionalität',
        'Kundenspezifische Preise',
        'Fortgeschrittene Berichterstattung',
        'Bestellhistorie & Analyse',
        'Prioritäts-Support',
        'Vierteljährliche Updates'
      ],
      limitations: [
        'Begrenzte individuelle Anpassungen',
        'Keine White-Label-Option'
      ],
      buttonText: 'Professional auswählen',
      featured: true
    },
    {
      title: 'Enterprise',
      description: 'Vollständig anpassbare Lösung für Unternehmen mit komplexen Anforderungen.',
      price: 'Individuell',
      period: 'nach Anfrage',
      features: [
        'Unbegrenzte Benutzer',
        'Alle Professional-Funktionen',
        'Vollständige Anpassbarkeit',
        'White-Label-Option',
        'Multi-ERP-Integration',
        'Umfassende API-Zugriffe',
        'Dedizierter Account Manager',
        'SLA-garantierter Support',
        'Monatliche Updates',
        'Individuelle Schulungen'
      ],
      limitations: [],
      buttonText: 'Angebot anfordern',
      featured: false
    }
  ];

  return (
    <section id="pricing" className="py-20 bg-blue-50">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-blue-900">
            Wähle das passende Paket für deinen Außendienst
          </h2>
          <p className="text-xl text-gray-700">
            Unsere transparenten Preismodelle bieten für jede Unternehmensgröße die richtige Lösung – mit monatlicher oder jährlicher Abrechnung.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <div 
              key={index}
              className={cn(
                "bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1",
                plan.featured && "ring-2 ring-blue-500 ring-offset-2 ring-offset-blue-50 md:scale-105"
              )}
            >
              {plan.featured && (
                <div className="bg-blue-600 text-white py-2 text-center text-sm font-semibold">
                  MEISTGEWÄHLT
                </div>
              )}
              
              <div className="p-8">
                <h3 className="text-2xl font-bold mb-2 text-gray-900">{plan.title}</h3>
                <p className="text-gray-600 mb-6 h-12">{plan.description}</p>
                
                <div className="mb-6">
                  <span className="text-4xl font-bold text-blue-600">{plan.price}</span>
                  <span className="text-gray-600 ml-2">{plan.period}</span>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start">
                      <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                  
                  {plan.limitations.map((limitation, lIndex) => (
                    <li key={`limitation-${lIndex}`} className="flex items-start opacity-70">
                      <svg className="h-5 w-5 text-red-500 mr-2 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-500">{limitation}</span>
                    </li>
                  ))}
                </ul>
                
                <Button
                  className={cn(
                    "w-full py-6 text-base font-semibold",
                    plan.featured ? "bg-blue-600 hover:bg-blue-700 text-white" : "border-2 border-blue-600 bg-transparent text-blue-600 hover:bg-blue-50"
                  )}
                  asChild
                >
                  <a href="#contact">{plan.buttonText}</a>
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="max-w-3xl mx-auto text-center mt-16">
          <p className="text-lg text-gray-700 mb-6">
            Alle Preise verstehen sich zzgl. MwSt. Bei jährlicher Zahlung gewähren wir 10% Rabatt.
          </p>
          <p className="text-blue-700 font-semibold">
            Nicht sicher, welches Paket das richtige ist? Wir beraten dich gerne!
          </p>
          <Button
            size="lg"
            className="mt-6 bg-blue-900 hover:bg-blue-800 text-white"
            asChild
          >
            <a href="#contact">Kostenlose Beratung vereinbaren</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PackagesSection; 